/**
 * CommandCentreMap - Trakpro-style Live Operations Map
 * Clear, bright, operationally readable map for security command centre.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import {
  MapPin, Navigation, Clock, Battery, AlertTriangle, CheckCircle2,
  Route, Eye, EyeOff, Radio, ChevronRight, Search, Filter,
  Layers, Target, Play, Pause, SkipBack, SkipForward, RefreshCw,
  Building2, AlertCircle, Zap, Car
} from 'lucide-react';
import OfficerAvatar from '@/components/common/OfficerAvatar';

// ============================================================================
// CONSTANTS
// ============================================================================

// CARTO Positron raster basemap.
// This browser key is restricted by CARTO to Rapid X domains.
// CARTO and OpenStreetMap attribution remains visible.
const CARTO_BASEMAP_KEY =
  process.env.REACT_APP_CARTO_BASEMAP_KEY || '';

const MAP_TILES = {
  rich: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  light: CARTO_BASEMAP_KEY
    ? 'https://{s}.basemaps.cartocdn.com/' +
      'rastertiles/light_all/{z}/{x}/{y}.png' +
      `?key=${encodeURIComponent(CARTO_BASEMAP_KEY)}`
    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  streets: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
};

// Status colors matching Trakpro
const STATUS_COLORS = {
  active: '#22c55e',      // Green - On patrol
  moving: '#22c55e',      // Green - Moving
  idle: '#eab308',        // Yellow - Idle/Stationary
  stationary: '#eab308',  // Yellow - Stationary
  alarm: '#ef4444',       // Red - Emergency
  emergency: '#ef4444',   // Red - Emergency
  offline: '#9ca3af',     // Grey - Offline
  stale: '#f97316',       // Orange - Stale GPS
};

// Trail time filters
const TRAIL_FILTERS = [
  { id: 'live', label: 'Live', minutes: 0 },
  { id: '30min', label: 'Last 30 min', minutes: 30 },
  { id: '2hours', label: 'Last 2 hours', minutes: 120 },
  { id: 'shift', label: 'Entire Shift', minutes: -1 },
];

// ============================================================================
// CUSTOM MARKER ICONS - Large, bright, labeled like Trakpro
// ============================================================================

const createOfficerMarker = (officer) => {
  const color = STATUS_COLORS[officer.status] || STATUS_COLORS[officer.movement_state] || STATUS_COLORS.active;
  const isStale = officer.is_stale;
  const borderColor = isStale ? '#f97316' : '#ffffff';
  
  // Direction arrow rotation
  const rotation = officer.heading || 0;
  
  return L.divIcon({
    className: 'officer-marker-trakpro',
    html: `
      <div class="officer-marker-container" style="position: relative; width: 60px;">
        <!-- Direction Arrow -->
        <div style="
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%) rotate(${rotation}deg);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 12px solid ${color};
          z-index: 2;
        "></div>
        
        <!-- Main Marker -->
        <div style="
          background: ${color};
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 4px solid ${borderColor};
          box-shadow: 0 3px 12px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          color: white;
          font-family: 'Rajdhani', sans-serif;
          margin: 0 auto;
        ">
          ${
            officer.profile_image || officer.profile_photo || officer.avatar_url || officer.photo_url
              ? `<img src="${officer.profile_image || officer.profile_photo || officer.avatar_url || officer.photo_url}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" />`
              : (officer.initials || '?')
          }
        </div>
        
        <!-- Label -->
        <div style="
          background: rgba(255,255,255,0.95);
          border: 1px solid ${color};
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 11px;
          font-weight: bold;
          color: #1f2937;
          white-space: nowrap;
          margin-top: 4px;
          text-align: center;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        ">
          ${officer.unit_id || officer.name?.split(' ')[0] || 'Unit'}
        </div>
      </div>
    `,
    iconSize: [60, 70],
    iconAnchor: [30, 50],
    popupAnchor: [0, -50],
  });
};

const createSiteMarker = (site) => {
  return L.divIcon({
    className: 'site-marker-trakpro',
    html: `
      <div style="
        background: #3b82f6;
        width: 28px;
        height: 28px;
        border-radius: 4px;
        border: 2px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

const createIncidentMarker = (incident) => {
  const color = incident.severity === 'high' ? '#ef4444' : incident.severity === 'medium' ? '#f97316' : '#eab308';
  return L.divIcon({
    className: 'incident-marker-trakpro',
    html: `
      <div style="
        background: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        animation: pulse 2s infinite;
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};


const normalizeOfficerForMap = (officer) => {
  const lat = Number(
    officer?.lat ??
    officer?.latitude ??
    officer?.gps_lat ??
    officer?.last_lat ??
    officer?.location?.lat ??
    officer?.location?.latitude ??
    officer?.last_location?.lat ??
    officer?.last_location?.latitude ??
    officer?.lastLocation?.lat ??
    officer?.lastLocation?.latitude ??
    officer?.gps?.lat ??
    officer?.gps?.latitude
  );

  const lng = Number(
    officer?.lng ??
    officer?.longitude ??
    officer?.gps_lng ??
    officer?.last_lng ??
    officer?.location?.lng ??
    officer?.location?.longitude ??
    officer?.last_location?.lng ??
    officer?.last_location?.longitude ??
    officer?.lastLocation?.lng ??
    officer?.lastLocation?.longitude ??
    officer?.gps?.lng ??
    officer?.gps?.longitude
  );

  return {
    ...officer,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
};


// ============================================================================
// MAP AUTO-FIT COMPONENT
// ============================================================================

function MapAutoFit({ officers, sites }) {
  const map = useMap();
  
  useEffect(() => {
    const allCoords = [
      ...(officers || []).filter(o => o.lat && o.lng).map(o => [o.lat, o.lng]),
      ...(sites || []).filter(s => s.latitude && s.longitude).map(s => [s.latitude, s.longitude]),
    ];
    
    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [officers?.length, sites?.length]);
  
  return null;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function PanicMapFocus({
  panicFocus,
  officers = [],
  onOfficerFocused,
  focusRequest = 0,
}) {
  const map = useMap();

  // The map should take over only ONCE for each emergency.
  // Subsequent GPS updates move the marker without stealing
  // control of the map from the Command Centre operator.
  const lastFocusedAlertRef = useRef(null);

  useEffect(() => {
    if (!panicFocus?.alert_id) {
      lastFocusedAlertRef.current = null;
      return;
    }

    const alertId = String(panicFocus.alert_id);

    // Automatic takeover happens once per emergency.
    // A manual focus request may intentionally run again.
    const focusKey = `${alertId}:${focusRequest}`;

    if (lastFocusedAlertRef.current === focusKey) {
      return;
    }

    // Prefer the officer's current live-tracking coordinates.
    // Fall back to the coordinates captured when Panic was pressed.
    const emergencyOfficer = (officers || []).find((officer) => {
      const officerId =
        officer?.id ||
        officer?._id ||
        officer?.officer_id ||
        officer?.user_id;

      return (
        officerId != null &&
        panicFocus.officer_id != null &&
        String(officerId) === String(panicFocus.officer_id)
      );
    });

    const liveLat = Number(
      emergencyOfficer?.lat ??
      emergencyOfficer?.latitude
    );

    const liveLng = Number(
      emergencyOfficer?.lng ??
      emergencyOfficer?.longitude
    );

    const panicLat = Number(panicFocus.latitude);
    const panicLng = Number(panicFocus.longitude);

    const latitude =
      Number.isFinite(liveLat) ? liveLat : panicLat;

    const longitude =
      Number.isFinite(liveLng) ? liveLng : panicLng;

    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude)
    ) {
      console.warn(
        '[V6.8B] Panic received but no usable GPS position was available',
        panicFocus
      );
      return;
    }

    lastFocusedAlertRef.current = focusKey;

    // Stop any map animation already underway before emergency takeover.
    map.stop();

    // Bring the emergency into immediate operational focus.
    map.flyTo(
      [latitude, longitude],
      18,
      {
        animate: true,
        duration: 1.25,
        easeLinearity: 0.25,
      }
    );

    if (emergencyOfficer && onOfficerFocused) {
      onOfficerFocused({
        ...emergencyOfficer,
        lat: latitude,
        lng: longitude,
        panic_active: true,
        panic_alert_id: alertId,
      });
    }

    console.info(
      '[V6.8B] Emergency map takeover',
      {
        alertId,
        officerId: panicFocus.officer_id,
        latitude,
        longitude,
      }
    );
  }, [
    map,
    panicFocus?.alert_id,
    panicFocus?.officer_id,
    panicFocus?.latitude,
    panicFocus?.longitude,
    officers,
    onOfficerFocused,
    focusRequest,
  ]);

  return null;
}


function ResizeMap({ expanded }) {
    const map = useMap();

    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
        },300);
    }, [expanded, map]);

    return null;
}

export function CommandCentreMap({
  officers = [],
  sites = [],
  incidents = [],
  locationHistory = [],
  siteVisits = [],
  panicFocus = null,
  onOfficerSelect,
  onRefresh,
  isLoading,
}) {
  // Map state
  const [mapStyle, setMapStyle] = useState('rich');
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapExpanded, setMapExpanded] = useState(false);

  // RAPID X V6.8C — Emergency Command Panel
  const [emergencyFocusRequest, setEmergencyFocusRequest] = useState(0);
  const [emergencyNow, setEmergencyNow] = useState(() => Date.now());
  
  // Layer visibility
  const [showOfficerTrails, setShowOfficerTrails] = useState(true);
  const [showGeofences, setShowGeofences] = useState(true);
  const [showCheckpoints, setShowCheckpoints] = useState(false);
  const [showIncidents, setShowIncidents] = useState(true);
  const [showSiteLabels, setShowSiteLabels] = useState(true);
  
  // Trail filter
  const [trailFilter, setTrailFilter] = useState('30min');
  
  // Replay state
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mapReadyOfficers = useMemo(
    () => (officers || []).map(normalizeOfficerForMap),
    [officers]
  );

  // RAPID X V6.8C — Emergency timing / live GPS intelligence
  useEffect(() => {
    if (!panicFocus) return undefined;

    setEmergencyNow(Date.now());

    const timer = setInterval(() => {
      setEmergencyNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [panicFocus?.alert_id]);

  const emergencyOfficer = useMemo(() => {
    if (!panicFocus?.officer_id) return null;

    return mapReadyOfficers.find((officer) => {
      const officerId =
        officer?.id ||
        officer?._id ||
        officer?.officer_id ||
        officer?.user_id;

      return (
        officerId != null &&
        String(officerId) === String(panicFocus.officer_id)
      );
    }) || null;
  }, [mapReadyOfficers, panicFocus?.officer_id]);

  const emergencyStartedAt =
    panicFocus?.timestamp ||
    panicFocus?.created_at ||
    null;

  const emergencyStartedMs = emergencyStartedAt
    ? new Date(emergencyStartedAt).getTime()
    : null;

  const emergencyElapsedSeconds =
    emergencyStartedMs && Number.isFinite(emergencyStartedMs)
      ? Math.max(
          0,
          Math.floor(
            (emergencyNow - emergencyStartedMs) / 1000
          )
        )
      : null;

  const formatEmergencyElapsed = (seconds) => {
    if (seconds == null) return 'Unknown';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }

    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }

    return `${secs}s`;
  };

  const emergencyGpsTimestamp =
    emergencyOfficer?.last_update ||
    emergencyOfficer?.last_location_at ||
    emergencyOfficer?.timestamp ||
    panicFocus?.gps_timestamp ||
    panicFocus?.timestamp ||
    null;

  const emergencyGpsMs = emergencyGpsTimestamp
    ? new Date(emergencyGpsTimestamp).getTime()
    : null;

  const emergencyGpsAgeSeconds =
    emergencyGpsMs && Number.isFinite(emergencyGpsMs)
      ? Math.max(
          0,
          Math.floor(
            (emergencyNow - emergencyGpsMs) / 1000
          )
        )
      : null;

  const emergencyAccuracy =
    emergencyOfficer?.accuracy ??
    panicFocus?.accuracy ??
    null;

  const emergencyStatus = String(
    panicFocus?.status || 'active'
  ).toLowerCase();

    const emergencyResponding =
      emergencyStatus === 'responding';

    const emergencyAcknowledged =
      emergencyStatus === 'acknowledged' ||
      emergencyResponding ||
      Boolean(panicFocus?.acknowledged_at);

  const gpsIsStale =
    emergencyGpsAgeSeconds != null &&
    emergencyGpsAgeSeconds > 60;

  // Filter officers by search
  const filteredOfficers = useMemo(() => {
    if (!searchQuery) return mapReadyOfficers;
    const query = searchQuery.toLowerCase();
    return mapReadyOfficers.filter(o => 
      o.name?.toLowerCase().includes(query) ||
      o.unit_id?.toLowerCase().includes(query) ||
      o.site_name?.toLowerCase().includes(query)
    );
  }, [mapReadyOfficers, searchQuery]);
  
  // Get officer status
  const getOfficerStatus = (officer) => {
    // P0 FIX: Use tracking_status from backend if available
    if (officer.tracking_status) {
      return officer.tracking_status;
    }
    // Fallback to is_stale flag
    if (officer.is_stale) return 'stale';
    // Fallback to seconds_since_update calculation
    if (officer.seconds_since_update > 3600) return 'offline';
    if (officer.seconds_since_update > 300) return 'stale';
    if (officer.movement_state === 'moving') return 'moving';
    if (officer.movement_state === 'stationary') return 'idle';
    return 'active';
  };
  
  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m ago`;
  };
  
  // Handle officer click
  const handleOfficerClick = (officer) => {
    setSelectedOfficer(officer);
    if (onOfficerSelect) {
      onOfficerSelect(officer);
    }
  };
  
  // Filter trail by time
  const getFilteredTrail = useCallback(() => {
    if (!locationHistory.length) return [];
    
    const filter = TRAIL_FILTERS.find(f => f.id === trailFilter);
    if (!filter || filter.minutes === -1) return locationHistory;
    if (filter.minutes === 0) return locationHistory.slice(-10); // Live - last 10 points
    
    const cutoff = Date.now() - filter.minutes * 60 * 1000;
    return locationHistory.filter(loc => new Date(loc.timestamp).getTime() > cutoff);
  }, [locationHistory, trailFilter]);
  
  const trailData = getFilteredTrail();
  
  // POST-P0: Data quality stats
  const sitesWithMissingCoords = sites.filter(s => s.location_missing || !s.gps_configured).length;
  const officersWithMissingGPS = officers.filter(o => o.gps_unavailable).length;
  const hasDataQualityIssues = sitesWithMissingCoords > 0 || officersWithMissingGPS > 0;
  
  return (
    <div
      className={`flex bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
        mapExpanded
          ? "fixed inset-4 z-[9999] h-auto"
          : "h-[calc(100vh-200px)] min-h-[600px]"
      }`}
    >
      {/* Left Control Panel - Trakpro Style */}
      <div
        className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 overflow-hidden ${
          mapExpanded
            ? "w-0 min-w-0 border-r-0"
            : "w-72 min-w-[18rem]"
        }`}
      >
        {/* Search */}
        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search officers, sites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
          </div>
        </div>
        
        {/* Layer Controls */}
        <div className="p-3 border-b border-gray-200 space-y-2">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            <Layers className="w-3 h-3 inline mr-1" />
            Map Layers
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox 
              checked={showOfficerTrails} 
              onCheckedChange={setShowOfficerTrails}
              className="border-gray-400 data-[state=checked]:bg-blue-500"
            />
            <span className="text-sm text-gray-700">Show officer trails</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox 
              checked={showGeofences} 
              onCheckedChange={setShowGeofences}
              className="border-gray-400 data-[state=checked]:bg-blue-500"
            />
            <span className="text-sm text-gray-700">Show geofences</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox 
              checked={showIncidents} 
              onCheckedChange={setShowIncidents}
              className="border-gray-400 data-[state=checked]:bg-blue-500"
            />
            <span className="text-sm text-gray-700">Show incidents</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox 
              checked={showSiteLabels} 
              onCheckedChange={setShowSiteLabels}
              className="border-gray-400 data-[state=checked]:bg-blue-500"
            />
            <span className="text-sm text-gray-700">Show site labels</span>
          </label>
        </div>
        
        {/* Trail Time Filter */}
        <div className="p-3 border-b border-gray-200">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            <Route className="w-3 h-3 inline mr-1" />
            Trail Filter
          </div>
          <div className="flex flex-wrap gap-1">
            {TRAIL_FILTERS.map(filter => (
              <Button
                key={filter.id}
                variant={trailFilter === filter.id ? 'default' : 'outline'}
                size="sm"
                className={`text-xs h-7 ${
                  trailFilter === filter.id 
                    ? 'bg-blue-500 text-white hover:bg-blue-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
                onClick={() => setTrailFilter(filter.id)}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Live Units List */}
        <div className="flex-1 overflow-hidden">
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <Radio className="w-3 h-3 inline mr-1" />
              Live Units ({filteredOfficers.length})
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
              className="h-6 w-6 p-0"
            >
              <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>

          <Button
            variant="outline"
            onClick={() => setMapExpanded(current => !current)}
            className="ml-2"
          >
            {mapExpanded ? "Exit Map" : "Full Map"}
          </Button>


          </div>
          
          <ScrollArea className="h-[calc(100%-40px)]">
            <div className="p-2 space-y-1">
              {filteredOfficers.map((officer) => {
                const status = getOfficerStatus(officer);
                const statusColor = STATUS_COLORS[status];
                const isSelected = selectedOfficer?.id === officer.id;
                
                return (
                  <div
                    key={officer.id || officer.attendance_id || officer.shift_id}
                    className={`p-2 rounded cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-blue-50 border border-blue-300' 
                        : 'bg-white border border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => handleOfficerClick(officer)}
                  >
                    <div className="flex items-center gap-2">
                      {/* Status dot */}
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: statusColor }}
                      />
                      
                      {/* Officer info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-900 truncate">
                          {officer.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {officer.site_name || 'No site'}
                        </div>
                      </div>
                      
                      {/* Speed/Status */}
                      <div className="text-right">
                        <div className="text-xs font-medium capitalize" style={{ color: statusColor }}>
                          {status}
                        </div>
                        <div className={`text-[10px] ${status === 'stale' || status === 'offline' ? 'text-amber-500' : 'text-gray-400'}`}>
                          {officer.last_update ? formatTimeAgo(officer.last_update) : 
                           officer.clock_in_time ? formatTimeAgo(officer.clock_in_time) : 
                           'No update'}
                        </div>
                        {/* P0 FIX: Show GPS warning if unavailable */}
                        {officer.gps_unavailable && (
                          <div className="text-[10px] text-red-400 flex items-center gap-0.5 justify-end">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            No GPS
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Battery indicator */}
                    {officer.battery_level && (
                      <div className="mt-1 flex items-center gap-1 text-[10px]">
                        <Battery className={`w-3 h-3 ${officer.battery_level < 20 ? 'text-red-500' : 'text-gray-400'}`} />
                        <span className={officer.battery_level < 20 ? 'text-red-500' : 'text-gray-400'}>
                          {officer.battery_level}%
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {filteredOfficers.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No officers found
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
        
        {/* Status Legend */}
        <div className="p-3 border-t border-gray-200 bg-white">
          <div className="text-xs font-semibold text-gray-600 mb-2">Status Legend</div>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.active }} />
              <span>Active</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.idle }} />
              <span>Idle</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.stale }} />
              <span>Stale</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.offline }} />
              <span>Offline</span>
            </div>
          </div>
        </div>
        
        {/* POST-P0: Data Quality Warnings */}
        {hasDataQualityIssues && (
          <div className="p-3 border-t border-amber-200 bg-amber-50">
            <div className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Data Quality Issues
            </div>
            {sitesWithMissingCoords > 0 && (
              <div className="text-xs text-amber-600">
                {sitesWithMissingCoords} site(s) missing GPS
              </div>
            )}
            {officersWithMissingGPS > 0 && (
              <div className="text-xs text-amber-600">
                {officersWithMissingGPS} officer(s) GPS unavailable
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Map Container */}
      <div className="flex-1 relative">

        {/* RAPID X V6.8C — Emergency Command Panel */}
        {panicFocus && (
          <div
            className="absolute left-4 top-4 z-[1000] w-[360px] max-w-[calc(100%-2rem)]"
            data-testid="emergency-command-panel"
          >
            <div className={
              emergencyAcknowledged
                ? 'rounded-2xl border-2 border-amber-400 bg-zinc-950/95 shadow-2xl shadow-amber-950/60 overflow-hidden'
                : 'rounded-2xl border-2 border-red-500 bg-zinc-950/95 shadow-2xl shadow-red-950/70 overflow-hidden'
            }>
              <div className={
                emergencyAcknowledged
                  ? 'bg-amber-500/15 px-4 py-3 border-b border-amber-400/30'
                  : 'bg-red-500/15 px-4 py-3 border-b border-red-500/30'
              }>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className={
                      emergencyAcknowledged
                        ? 'text-xs font-black uppercase tracking-[0.18em] text-amber-300'
                        : 'text-xs font-black uppercase tracking-[0.18em] text-red-300'
                    }>
                      🚨 Emergency Command
                    </div>

                    <div className="mt-1 text-xl font-black text-white">
                      {panicFocus.officer_name || 'Officer'}
                    </div>
                  </div>

                    <Badge className={
                      emergencyResponding
                        ? 'bg-yellow-500/20 text-yellow-200 border-yellow-400/50'
                        : emergencyAcknowledged
                        ? 'bg-amber-500/20 text-amber-200 border-amber-400/50'
                        : 'bg-red-500/20 text-red-200 border-red-400/50 animate-pulse'
                    }>
                      {emergencyResponding
                        ? 'RESPONDING'
                        : emergencyAcknowledged
                        ? 'ACKNOWLEDGED'
                        : 'ACTIVE'}
                    </Badge>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="text-sm text-zinc-300">
                  <MapPin className="inline-block h-4 w-4 mr-1 text-red-400" />
                  {panicFocus.site_name ||
                    emergencyOfficer?.site_name ||
                    'Live officer location'}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-zinc-900 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Emergency elapsed
                    </div>
                    <div className="mt-1 font-bold text-white tabular-nums">
                      {formatEmergencyElapsed(
                        emergencyElapsedSeconds
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl bg-zinc-900 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      GPS age
                    </div>
                    <div className={
                      gpsIsStale
                        ? 'mt-1 font-bold text-orange-300'
                        : 'mt-1 font-bold text-emerald-300'
                    }>
                      {emergencyGpsAgeSeconds == null
                        ? 'Unknown'
                        : `${emergencyGpsAgeSeconds}s ago`}
                    </div>
                  </div>

                  <div className="rounded-xl bg-zinc-900 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      GPS accuracy
                    </div>
                    <div className="mt-1 font-bold text-white">
                      {emergencyAccuracy != null
                        ? `${Math.round(
                            Number(emergencyAccuracy)
                          )} m`
                        : 'Unknown'}
                    </div>
                  </div>

                  <div className="rounded-xl bg-zinc-900 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Response status
                    </div>
                      <div className={
                        emergencyResponding
                          ? 'mt-1 font-bold text-yellow-300'
                          : emergencyAcknowledged
                          ? 'mt-1 font-bold text-amber-300'
                          : 'mt-1 font-bold text-red-300'
                      }>
                        {emergencyResponding
                          ? 'Control Room responding'
                          : emergencyAcknowledged
                          ? 'Control Room acknowledged'
                          : 'Awaiting acknowledgement'}
                      </div>
                  </div>
                </div>

                {gpsIsStale && (
                  <div className="rounded-xl border border-orange-500/40 bg-orange-950/40 px-3 py-2 text-xs text-orange-200">
                    <AlertTriangle className="inline-block h-4 w-4 mr-1" />
                    Last known officer location is more than 60 seconds old.
                  </div>
                )}

                {panicFocus.acknowledged_by_name && (
                  <div className="rounded-xl bg-zinc-900 px-3 py-2 text-xs text-zinc-300">
                    Acknowledged by{' '}
                    <span className="font-bold text-white">
                      {panicFocus.acknowledged_by_name}
                    </span>
                  </div>
                )}

                <Button
                  type="button"
                  onClick={() =>
                    setEmergencyFocusRequest(
                      (current) => current + 1
                    )
                  }
                  className="w-full h-11 bg-red-600 hover:bg-red-500 text-white font-black"
                >
                  <Target className="h-4 w-4 mr-2" />
                  FOCUS ON OFFICER
                </Button>

                <div className="text-center text-[10px] uppercase tracking-wider text-zinc-600">
                  Live GPS continues updating without forcing map position
                </div>
              </div>
            </div>
          </div>
        )}

        <MapContainer
          center={[-32.9283, 151.7817]}
          zoom={10}
          minZoom={8}
          maxZoom={18}
          className="h-full w-full"
          style={{ background: '#f8fafc' }}
        >
        <ResizeMap expanded={mapExpanded} />

        <PanicMapFocus
          panicFocus={panicFocus}
          officers={mapReadyOfficers}
          focusRequest={emergencyFocusRequest}
          onOfficerFocused={(officer) => {
            setSelectedOfficer(officer);
            onOfficerSelect?.(officer);
          }}
        />

          {/* Light Map Tiles - Carto Light for Trakpro clarity */}
          <TileLayer
            attribution={mapStyle === 'satellite'
              ? 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community'
              : mapStyle === 'light' && CARTO_BASEMAP_KEY
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'}
            url={MAP_TILES[mapStyle]}
            maxZoom={19}
          />
          
          {/* Auto-fit bounds */}
          {!panicFocus && (
            <MapAutoFit
              officers={filteredOfficers}
              sites={sites}
            />
          )}
          
          {/* Site Geofences */}
          {showGeofences && sites.map((site) => (
            (site.lat || site.latitude) && (site.lng || site.longitude) && (
              <Circle
                key={`geofence-${site.id}`}
                center={[site.lat || site.latitude, site.lng || site.longitude]}
                radius={site.geofence?.radius || site.geofence_radius || 150}
                pathOptions={{
                  color: site.gps_configured === false ? '#f59e0b' : '#3b82f6',  // Amber for unconfigured
                  fillColor: site.gps_configured === false ? '#f59e0b' : '#3b82f6',
                  fillOpacity: 0.1,
                  weight: 2,
                  dashArray: site.gps_configured === false ? '3, 3' : '5, 5',
                }}
              />
            )
          ))}
          
          {/* Site Markers */}
          {showSiteLabels && sites.map((site) => (
            (site.lat || site.latitude) && (site.lng || site.longitude) && (
              <Marker
                key={`site-${site.id}`}
                position={[site.lat || site.latitude, site.lng || site.longitude]}
                icon={createSiteMarker(site)}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-bold text-blue-700 flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {site.name}
                      {site.gps_configured === false && (
                        <span className="text-amber-500 text-xs">(No GPS)</span>
                      )}
                    </div>
                    {site.address && (
                      <div className="text-xs text-gray-600 mt-1">{site.address}</div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      Geofence: {site.geofence?.radius || site.geofence_radius || 150}m
                    </div>
                    {site.gps_configured === false && (
                      <div className="text-xs text-amber-600 mt-1 font-medium">
                        ⚠️ GPS coordinates not configured
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Incident Markers */}
          {showIncidents && incidents.map((incident) => (
            incident.lat && incident.lng && (
              <Marker
                key={`incident-${incident.id}`}
                position={[incident.lat, incident.lng]}
                icon={createIncidentMarker(incident)}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-bold text-red-700 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {incident.title || incident.incident_type}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {incident.site_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Status: {incident.status}
                    </div>
                  </div>
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Officer Breadcrumb Trails */}
          {showOfficerTrails && trailData.length > 1 && (
            <Polyline
              positions={trailData.map(loc => [loc.latitude, loc.longitude])}
              pathOptions={{
                color: selectedOfficer ? STATUS_COLORS[getOfficerStatus(selectedOfficer)] : '#3b82f6',
                weight: 4,
                opacity: 0.9,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
          )}
          
          {/* P0 Panic emergency marker */}
          {panicFocus &&
            Number.isFinite(Number(panicFocus.latitude)) &&
            Number.isFinite(Number(panicFocus.longitude)) && (
              <Circle
                center={[
                  Number(panicFocus.latitude),
                  Number(panicFocus.longitude)
                ]}
                radius={35}
                pathOptions={{
                  color: '#dc2626',
                  fillColor: '#ef4444',
                  fillOpacity: 0.45,
                  weight: 5,
                }}
              >
                <Popup>
                  <div className="min-w-[240px]">
                    <div className="text-base font-black text-red-700">
                      🚨 ACTIVE PANIC ALERT
                    </div>

                    <div className="mt-2 font-bold text-gray-900">
                      {panicFocus.officer_name || 'Officer'}
                    </div>

                    <div className="text-sm text-gray-600">
                      {panicFocus.site_name || 'Location captured from device'}
                    </div>

                    <div className="mt-2 text-xs text-gray-500">
                      GPS accuracy:{' '}
                      {panicFocus.accuracy
                        ? `${Math.round(panicFocus.accuracy)} metres`
                        : 'Unknown'}
                    </div>

                    <div className="mt-1 font-mono text-xs text-gray-500">
                      {Number(panicFocus.latitude).toFixed(6)},{' '}
                      {Number(panicFocus.longitude).toFixed(6)}
                    </div>
                  </div>
                </Popup>
              </Circle>
            )}

          {/* Officer Markers - P0 FIX: Only render officers with valid coordinates */}
          {filteredOfficers.map((officer) => (
            officer.lat && officer.lng && (
              <Marker
                key={`officer-${officer.id || officer.attendance_id || officer.shift_id}`}
                position={[officer.lat, officer.lng]}
                icon={createOfficerMarker({
                  ...officer,
                  status: getOfficerStatus(officer),
                })}
                eventHandlers={{
                  click: () => handleOfficerClick(officer),
                }}
              >
                <Popup>
                  <div className="min-w-[250px] p-1">
                    <div className="font-bold text-gray-900 text-base flex items-center gap-2">
                      {officer.name}
                      {/* P0 FIX: Show tracking status badges */}
                      {(officer.tracking_status === 'stale' || officer.is_stale) && (
                        <span className="text-orange-500 text-xs bg-orange-100 px-1 rounded">(Stale)</span>
                      )}
                      {officer.tracking_status === 'offline' && (
                        <span className="text-red-500 text-xs bg-red-100 px-1 rounded">(Offline)</span>
                      )}
                    </div>
                    
                    <table className="w-full mt-2 text-sm">
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="text-gray-500 py-1">Status</td>
                          <td className="text-right font-medium capitalize">
                            {officer.tracking_status || getOfficerStatus(officer)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="text-gray-500 py-1">Site</td>
                          <td className="text-right font-medium">{officer.site_name || '-'}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="text-gray-500 py-1">Shift Stage</td>
                          <td className="text-right font-medium">{officer.workflow_stage || '-'}</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="text-gray-500 py-1">Last Update</td>
                          <td className={`text-right font-medium ${officer.is_stale ? 'text-orange-500' : ''}`}>
                            {officer.last_update ? formatTimeAgo(officer.last_update) : 
                             officer.clock_in_time ? formatTimeAgo(officer.clock_in_time) : '-'}
                          </td>
                        </tr>
                        {officer.battery_level && (
                          <tr className="border-b border-gray-100">
                            <td className="text-gray-500 py-1">Battery</td>
                            <td className={`text-right font-medium ${officer.battery_level < 20 ? 'text-red-500' : ''}`}>
                              {officer.battery_level}%
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td className="text-gray-500 py-1">Status</td>
                          <td className="text-right">
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium text-white"
                              style={{ backgroundColor: STATUS_COLORS[getOfficerStatus(officer)] }}
                            >
                              {getOfficerStatus(officer).toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Popup>
              </Marker>
            )
          ))}
        </MapContainer>
        
        {/* Map Controls Overlay - Top Right */}
        <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
          {/* Map Style Switcher */}
          <div className="bg-white rounded shadow-lg p-1 flex gap-1">
            <Button
              variant={mapStyle === 'light' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 text-xs"
              onClick={() => setMapStyle('light')}
            >
              Light
            </Button>
            <Button
              variant={mapStyle === 'rich' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 text-xs"
              onClick={() => setMapStyle('rich')}
            >
              Rich
            </Button>
            <Button
              variant={mapStyle === 'satellite' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 text-xs"
              onClick={() => setMapStyle('satellite')}
            >
              Satellite
            </Button>
          </div>
        </div>
        
        {/* Stats Overlay - Bottom Left */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur rounded shadow-lg p-3">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="font-medium">{filteredOfficers.filter(o => !o.is_stale).length}</span>
              <span className="text-gray-500">Active</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="font-medium">{filteredOfficers.filter(o => o.movement_state === 'stationary').length}</span>
              <span className="text-gray-500">Idle</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <span className="font-medium">{filteredOfficers.filter(o => o.is_stale).length}</span>
              <span className="text-gray-500">Stale</span>
            </div>
          </div>
        </div>
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-[1001] flex items-center justify-center">
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-lg px-4 py-2">
              <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Updating...</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Right Detail Panel - Shows when officer selected */}
      {selectedOfficer && (
        <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Officer Details</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedOfficer(null)}
              className="h-8 w-8 p-0"
            >
              ×
            </Button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Officer Info */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <OfficerAvatar
                  user={{
                    ...selectedOfficer,
                    first_name: selectedOfficer.first_name || selectedOfficer.name?.split(' ')?.[0],
                    last_name: selectedOfficer.last_name || selectedOfficer.name?.split(' ')?.slice(1).join(' '),
                  }}
                  size="lg"
                  className="ring-2 ring-white shadow-sm"
                />
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white"
                  style={{ backgroundColor: STATUS_COLORS[getOfficerStatus(selectedOfficer)] }}
                />
              </div>
              <div>
                <div className="font-bold text-gray-900">{selectedOfficer.name}</div>
                <div className="text-sm text-gray-500">{selectedOfficer.unit_id || 'No Unit ID'}</div>
              </div>
            </div>
            
            {/* Status Badge */}
            <div>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: STATUS_COLORS[getOfficerStatus(selectedOfficer)] }}
              >
                {getOfficerStatus(selectedOfficer).toUpperCase()}
              </span>
              {selectedOfficer.is_stale && (
                <span className="ml-2 px-2 py-1 rounded bg-orange-100 text-orange-700 text-xs font-medium">
                  GPS Stale
                </span>
              )}
            </div>
            
            {/* Details Grid */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Current Site</span>
                <span className="font-medium text-gray-900">{selectedOfficer.site_name || '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Clock In</span>
                <span className="font-medium text-gray-900">
                  {selectedOfficer.clock_in_time 
                    ? new Date(selectedOfficer.clock_in_time).toLocaleTimeString() 
                    : '-'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Elapsed</span>
                <span className="font-medium text-gray-900">{selectedOfficer.elapsed_hours || 0}h</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Speed</span>
                <span className="font-medium text-gray-900">
                  {selectedOfficer.speed ? `${(selectedOfficer.speed * 3.6).toFixed(0)} km/h` : 'Stationary'}
                </span>
              </div>
              {selectedOfficer.battery_level && (
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Battery</span>
                  <span className={`font-medium ${selectedOfficer.battery_level < 20 ? 'text-red-500' : 'text-gray-900'}`}>
                    {selectedOfficer.battery_level}%
                  </span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Coordinates</span>
                <span className="font-mono text-xs text-gray-700">
                  {selectedOfficer.lat?.toFixed(5)}, {selectedOfficer.lng?.toFixed(5)}
                </span>
              </div>
            </div>
            
            {/* Site Visits */}
            {siteVisits.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Sites Visited ({siteVisits.length})
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {siteVisits.map((visit, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs"
                    >
                      <div className="flex items-center gap-2">
                        {visit.is_current ? (
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        ) : (
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                        )}
                        <span className="font-medium">{visit.site_name}</span>
                      </div>
                      <span className="text-gray-500">
                        {visit.dwell_time_formatted || (visit.is_current ? 'Now' : '-')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Add marker animation styles
const MarkerStyles = () => (
  <style>{`
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.1); opacity: 0.8; }
    }
    
    .officer-marker-trakpro {
      background: transparent !important;
      border: none !important;
    }
    
    .site-marker-trakpro {
      background: transparent !important;
      border: none !important;
    }
    
    .incident-marker-trakpro {
      background: transparent !important;
      border: none !important;
    }
    
    .leaflet-popup-content-wrapper {
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }
    
    .leaflet-popup-content {
      margin: 8px;
    }
  `}</style>
);

export { MarkerStyles };
export default CommandCentreMap;
